import axios from 'axios';
import { useEffect, useState } from 'react';
import { Environment, Network, RecordSource, Store } from 'relay-runtime';

import { composeMiddlewares } from '../compose-middlewares';
import { getAvailableServices } from '../get-available-services';
import { EnvironmentConfigType, MiddlewareCreatorType } from '../relay-types';
import { tokenRefresher } from '../token-refresher';

const KNOWN_ENVS = {
    production: {
        availableServiceUrl: 'https://srv-prod-rapi.sitemax.app/prod/get-available-service',
    },
    development: {
        availableServiceUrl: 'https://srv-dev-rapi.sitemax.io/prod/get-available-service',
    },
};

const createAxiosInstance = (envConfig: EnvironmentConfigType) => {
    const glob = window ?? global;

    const instance = axios.create();
    instance.interceptors.request.use(
        async (config) => {
            config.withCredentials = false;

            const freshToken = await tokenRefresher(envConfig);
            config.headers.Authorization = config.headers.Authorization || `Bearer ${freshToken}`;
            return config;
        },
        (error) => Promise.reject(error),
    );
    (glob as any).soixa = instance;
    return instance;
};

export const useEnvironment = (config: EnvironmentConfigType, middlewareCreators: MiddlewareCreatorType[]) => {
    const [relayEnvironment, setEnvironment] = useState<Environment | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { envName } = config;
    const knownEnvironment = KNOWN_ENVS[envName];

    useEffect(() => {
        let isCancelled = false;

        (async () => {
            try {
                if (!knownEnvironment) {
                    setError(new Error(`Unknown environment: ${envName}`));
                    setIsLoading(false);
                    return;
                }

                if (config.authUrl.includes('https:///') || config.authUrl.includes('undefined')) {
                    setError(new Error(`Auth url is not properly set: ${config.authUrl}`));
                    setIsLoading(false);
                    return;
                }

                setIsLoading(true);
                setError(null);

                const refreshedToken = config.devToken ?? (await tokenRefresher(config));
                const middlewareTokenRefresher = () => tokenRefresher(config);

                const availableServices = await getAvailableServices({
                    availableServicesUrl: knownEnvironment.availableServiceUrl,
                    authToken: refreshedToken,
                });
                const middlewares = middlewareCreators.map((creator) =>
                    creator(availableServices, middlewareTokenRefresher, config.devToken),
                );

                const relayFetch = composeMiddlewares(middlewares);

                const relayEnvironment = new Environment({
                    network: Network.create(relayFetch),
                    store: new Store(new RecordSource()),
                });

                if (!isCancelled) {
                    setEnvironment(relayEnvironment);
                    createAxiosInstance(config);
                }
            } catch (err) {
                if (!isCancelled) {
                    setError(err instanceof Error ? err : new Error('Unknown error'));
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [config, middlewareCreators]);

    return {
        relayEnvironment,
        isLoading,
        error,
    };
};
